
import Vue from 'vue'
import FormButtons from '@/components/userProfile/FormButtons.vue'
import Select from '@/components/atoms/Select.vue'
import ToggleSwitch from '@/components/atoms/ToggleSwitch.vue'
import DatePicker from '@/components/atoms/DatePicker.vue'
import { UserProfile } from 'vue/types/vue'
import STATE_ISO_MAP from '@/plugins/states'
import { FETCH_CAREER_INFORMATION, UPDATE_PERSONAL_INFO } from '@/graphql.js'
import moment from 'moment'

const DAY_MONTH_YEAR_FORMAT = 'MM-DD-YYYY'

export default Vue.extend({
  name: 'Career',
  data: () => {
    const initialFormState = {
      estimatedProgramCompletionDate: '',
      isSeekingEmployment: false,
      isSeekingRemote: false,
      isSeekingOnsite: true,
      isSeekingExternship: false,
      isPartTime: false,
      isFullTime: true,
      location: {
        label: '',
        placeId: undefined as string | undefined,
        terms: [],
      },
      distanceWithinMiles: Vue.Constants.DISTANCES[1],
    }

    return ({
      savedSuccessfully: false,
      saveError: false,
      showRequiredFieldsDialog: false,
      showNavigationWarning: false,
      error: '',
      toastXPosition: 0,
      toastYPosition: 0,
      userProfile: {} as UserProfile,
      initialFormState,
      form: initialFormState,
      distances: Vue.Constants.DISTANCES,
      stateEnums: STATE_ISO_MAP,
    })
  },
  components: {
    FormButtons,
    ToggleSwitch,
    DatePicker,
    Select,
  },
  computed: {
    hasEdits (): boolean {
      const { career } = this.userProfile
      return (
        !moment(this.form.estimatedProgramCompletionDate, 'MM/DD/YYYY').isSame(moment(career?.estimatedProgramCompletionDate)) ||
        this.form.isSeekingEmployment !== (career?.isSeekingEmployment ?? this.initialFormState.isSeekingEmployment) ||
        this.form.isSeekingOnsite !== (career?.isSeekingOnsite ?? this.initialFormState.isSeekingOnsite) ||
        this.form.isSeekingRemote !== (career?.isSeekingRemote ?? this.initialFormState.isSeekingRemote) ||
        this.form.location.placeId !== (career?.location?.googlePlaceId || undefined) ||
        this.form.distanceWithinMiles.value !== (career?.distanceWithinMiles || this.initialFormState.distanceWithinMiles.value) ||
        this.form.isSeekingExternship !== (career?.isSeekingExternship ?? false) ||
        this.form.isFullTime !== (career?.isFullTime ?? this.initialFormState.isFullTime) ||
        this.form.isPartTime !== (career?.isPartTime ?? this.initialFormState.isPartTime)
      )
    },
  },
  watch: {
    userProfile: function (value) {
      this.setDataFromProfile(value)
    },
  },
  apollo: {
    userProfile: {
      query: FETCH_CAREER_INFORMATION,
      variables: function () {
        if (this.$route.params?.masqueradeUser) {
          return { input: this.$route.params?.masqueradeUser }
        }
      },
      update: data => data.user,
    }
  },
  methods: {
    setDataFromProfile: function (userProfile: UserProfile) {
      const { career } = userProfile
      const workMileRadius = career?.distanceWithinMiles || this.initialFormState.distanceWithinMiles
      let locationLabel = ''
      if (career?.location?.googlePlaceId) {
        const { location } = career
        const state = STATE_ISO_MAP[location.stateIsoCode]
        const country = 'USA'
        if (location.postalCode) {
          locationLabel = `${location.city}, ${state} ${location.postalCode}, ${country}`
        } else {
          locationLabel = `${location.city}, ${state}, ${country}`
        }
      }
      this.form = {
        estimatedProgramCompletionDate: moment(career?.estimatedProgramCompletionDate).format(DAY_MONTH_YEAR_FORMAT),
        isSeekingEmployment: career?.isSeekingEmployment ?? this.initialFormState.isSeekingEmployment,
        isSeekingOnsite: career?.isSeekingOnsite ?? this.initialFormState.isSeekingOnsite,
        isSeekingRemote: career?.isSeekingRemote ?? this.initialFormState.isSeekingRemote,
        location: {
          label: locationLabel,
          placeId: career?.location?.googlePlaceId || undefined,
          terms: [],
        },
        isSeekingExternship: career?.isSeekingExternship ?? this.initialFormState.isSeekingExternship,
        distanceWithinMiles: this.distances.find(distance => distance.value === workMileRadius) || this.distances[1],
        isFullTime: career?.isFullTime ?? this.initialFormState.isFullTime,
        isPartTime: career?.isPartTime ?? this.initialFormState.isPartTime,
      }
    },
    cancel: function () {
      // Revert fields
      this.setDataFromProfile(this.userProfile)
    },
    getLocationFields: function (addressComponents: google.maps.places.PredictionTerm[]) {
      // If we expand to include locations outside of the US we may need to use the Place Details API
      // Currently finding values from information returned by Place Autocomplete since it is cheaper
      if (addressComponents?.length) {
        let hasZipCode = addressComponents.length === 4
        return {
          city: addressComponents[0]?.value,
          stateName: addressComponents[1].value,
          postalCode: hasZipCode ? addressComponents[2]?.value : '',
        }
      }
      return null
    },

    save: async function () {
      this.saveError = false
      try {
        const locationFields = this.getLocationFields(this.form.location.terms)
        const location = locationFields === null
          ? null
          : {
            city: locationFields.city || this.userProfile.career?.location?.city,
            stateIsoCode: `US-${locationFields.stateName}` || this.userProfile.career?.location?.stateIsoCode,
            postalCode: locationFields.postalCode || '',
            googlePlaceId: this.form.location.placeId || ''
          }
        const updates = {
          career: this.form.isSeekingEmployment
            ? {
              ...this.form,
              distanceWithinMiles: this.form.distanceWithinMiles.value,
              location,
            }
            : {
              estimatedProgramCompletionDate: this.form.estimatedProgramCompletionDate,
              isSeekingEmployment: false,
              isSeekingOnsite: false,
              isSeekingRemote: false,
              isSeekingExternship: false,
              isFullTime: false,
              isPartTime: false,
              location: null,
              distanceWithinMiles: null,
            }
        }
        await this.$apollo.mutate({
          mutation: UPDATE_PERSONAL_INFO,
          variables: { uuid: this.userProfile.uuid, updates: updates }
        })
        this.$apollo.queries.userProfile.refresh()
        this.savedSuccessfully = true
      } catch (e) {
        this.error = (e as Error).message
        this.saveError = true
      }
    },
    requiredFieldIsMissing: function () {
      return (
        this.form.estimatedProgramCompletionDate.length !== 10 ||
        !this.getDateAsISOString(this.form.estimatedProgramCompletionDate).isValid ||
        (this.form.isSeekingEmployment && !this.form.location.placeId) ||
        (this.form.isSeekingEmployment && !(this.form.isPartTime || this.form.isFullTime))
      )
    },
    getFormattedDate: function (dateISOString?: string) {
      if (dateISOString) {
        let date = new Date(dateISOString)
        let month = (1 + date.getMonth()).toString().padStart(2, '0')
        let day = (date.getDate()).toString().padStart(2, '0')
        let year = date.getFullYear()
        return month + '/' + day + '/' + year
      }
      return ''
    },
    getDateAsISOString: function (dateString: string) {
      if (dateString.length) {
        let month = parseInt(dateString.substring(0, 2)) - 1
        let day = parseInt(dateString.substring(3, 5))
        let year = parseInt(dateString.substring(6, 10))
        return { value: moment([year, month, day]).toISOString(), isValid: moment([year, month, day]).isValid() }
      }
      return { value: '', isValid: false }
    },
    getDistanceFromString: function (distanceString: string) {
      return this.distances.find(distance => distance.name === distanceString)?.value
    },
  },
  beforeRouteLeave (to, from, next) {
    if (this.$route.params?.masqueradeUser) {
      next()
    }
    if (this.hasEdits) {
      this.$emit('hideNavMenu')
      this.showNavigationWarning = true
    } else {
      next()
    }
  }
})
